import { nextBlockPosition } from "./layout";
import type { ChartBlockSpec, ChartVizSpec, QuerySpec, ReportSpec } from "./schema";

interface BuildSolaChartBlockInput {
  blockId: string;
  title: string;
  businessQuestion?: string;
  explanation?: string;
  query: QuerySpec;
  viz: ChartVizSpec;
  existingCount: number;
}

const WIDE_CHARTS = new Set([
  "calendar_heatmap",
  "cohort",
  "funnel",
  "heatmap",
  "sankey",
  "stacked_bar",
  "treemap",
]);

const COMPACT_CHARTS = new Set(["gauge", "pie", "donut"]);

export function buildSolaChartBlock({
  blockId,
  title,
  businessQuestion,
  explanation,
  query,
  viz,
  existingCount,
}: BuildSolaChartBlockInput): ChartBlockSpec {
  const dimensions = layoutForChart(viz.type);
  const base = nextBlockPosition(existingCount);
  const wraps = base.x + dimensions.w > 12;
  const x = wraps ? 0 : base.x;
  const y = wraps ? base.y + 2 : base.y;

  const resolvedBusinessQuestion = businessQuestion || explanation || title || "SOLA generated this chart.";

  return {
    id: blockId,
    type: "chart",
    title: title || "SOLA chart",
    layout: { x, y, ...dimensions },
    query,
    viz,
    intent: {
      audience: "recruiter",
      businessQuestion: resolvedBusinessQuestion,
      ambiguityStatus: "resolved",
    },
    narrative: explanation
      ? {
          summary: explanation,
          claims: [],
          audienceSafe: false,
        }
      : undefined,
  };
}

export function appendBlockToFirstReportPage(spec: ReportSpec, block: ChartBlockSpec): ReportSpec {
  const [firstPage, ...restPages] = spec.pages;
  if (!firstPage) throw new Error("This dashboard has no page to add the chart to.");

  return {
    ...spec,
    pages: [
      {
        ...firstPage,
        blocks: [...firstPage.blocks, block],
      },
      ...restPages,
    ],
  };
}

function layoutForChart(type: ChartVizSpec["type"]): { w: number; h: number; minW: number; minH: number } {
  if (COMPACT_CHARTS.has(type)) return { w: 4, h: 4, minW: 3, minH: 3 };
  if (WIDE_CHARTS.has(type)) return { w: 8, h: 5, minW: 4, minH: 4 };
  if (type === "radar" || type === "boxplot") return { w: 6, h: 5, minW: 4, minH: 4 };
  return { w: 6, h: 4, minW: 4, minH: 3 };
}
